import { clientConfigurationProvider as getConfigValue } from '@ocean/configs'
import { LoggerUtils } from '@ocean/utils'
import { SCORM12Status, SuccessStatus, CompletionStatus, Scorm2004Statuses } from 'scorm/types'

export const convertToScormTimeSpan = (inputSeconds: number) => {
  // CMITimespan => HHHH:MM:SS.SS
  var hours = Math.floor(inputSeconds / 3600)
  var minutes = Math.floor(inputSeconds / 60) % 60
  var seconds = inputSeconds % 60

  const formattedHours = hours.toString().padStart(4, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedSeconds = seconds.toString().padStart(2, '0')

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}.00`
}

export const convertFromScormTimeToSeconds = (scormTime: string) => {
  if (scormTime.length < 8 || scormTime.length > 13) {
    throw Error('Incorrect input value.')
  }
  const tsParts = scormTime.split(':')
  if (tsParts.length !== 3) {
    throw Error('Incorrect input value.')
  }
  const hours = parseInt(tsParts[0])
  const minutes = parseInt(tsParts[1])
  const seconds = parseInt(tsParts[2])

  const secondsFromHours = isNaN(hours) ? 0 : hours * 60 * 60
  const secondsFromMinutes = isNaN(minutes) ? 0 : minutes * 60
  const secondsFromSeconds = isNaN(seconds) ? 0 : seconds

  return secondsFromHours + secondsFromMinutes + secondsFromSeconds
}

export const getScorm12Status = (
  completionStatus: CompletionStatus,
  successStatus: SuccessStatus
): SCORM12Status => {
  if (
    completionStatus === CompletionStatus.NotAttempted &&
    successStatus === SuccessStatus.Unknown
  ) {
    return SCORM12Status.NotAttempted
  }

  if (completionStatus === CompletionStatus.Incomplete && successStatus === SuccessStatus.Unknown) {
    return SCORM12Status.Incomplete
  }

  if (completionStatus === CompletionStatus.Completed && successStatus === SuccessStatus.Unknown) {
    return SCORM12Status.Completed
  }

  if (completionStatus === CompletionStatus.Completed && successStatus === SuccessStatus.Passed) {
    return SCORM12Status.Passed
  }

  if (completionStatus === CompletionStatus.Completed && successStatus === SuccessStatus.Failed) {
    return SCORM12Status.Failed
  }

  throw new Error('Unknown status')
}

export const getScorm2004Statuses = (status: SCORM12Status): Scorm2004Statuses => {
  switch (status) {
    case SCORM12Status.NotAttempted:
      return {
        completionStatus: CompletionStatus.NotAttempted,
        successStatus: SuccessStatus.Unknown,
      }
    case SCORM12Status.Incomplete:
      return {
        completionStatus: CompletionStatus.Incomplete,
        successStatus: SuccessStatus.Unknown,
      }
    case SCORM12Status.Completed:
      return {
        completionStatus: CompletionStatus.Completed,
        successStatus: SuccessStatus.Unknown,
      }
    case SCORM12Status.Passed:
      return {
        completionStatus: CompletionStatus.Completed,
        successStatus: SuccessStatus.Passed,
      }
    case SCORM12Status.Failed:
      return {
        completionStatus: CompletionStatus.Completed,
        successStatus: SuccessStatus.Failed,
      }
    default:
      throw Error('Unknown status')
  }
}

export const convertToScormDisplayTime = (seconds: number) => {
  const date = new Date(0)
  date.setSeconds(seconds)
  return date.toISOString().substr(11, 8)
}

export const createRapidModuleLocation = (
  courseId: string,
  revision: number,
  scoLocation: string
) => {
  const resourcesVirtualDirectoryName = getConfigValue('resourcesVirtualDirectoryName')
  const resourcesRepidElearningLocation = getConfigValue('resourcesRepidElearningLocation')
  const rapidBaseUrl = `${resourcesVirtualDirectoryName}/${resourcesRepidElearningLocation}`

  return `/${rapidBaseUrl}/${courseId}/${revision}/${scoLocation}`
}

export const interceptAfter = (object: any, methodName: string, fn: any) => {
  const originalMethod = object[methodName]
  object[methodName] = function () {
    if (!originalMethod) {
      LoggerUtils.warn('Original method does not exist.')
      return
    }
    try {
      originalMethod.apply(object, arguments)
    } catch (e) {
      LoggerUtils.warn('Original method failure.')
    }
    fn.call(object)
  }
}

export const openWindow = (
  scoLocation: string,
  windowName: string,
  width: number,
  height: number,
  canBeResized: boolean,
  canBeMaximized: boolean
) => {
  const { top } = window as any
  const { outerHeight, screenY, outerWidth, screenX } = top
  const y = outerHeight / 2 + screenY - height / 2
  const x = outerWidth / 2 + screenX - width / 2
  return window.open(
    `scorm-module.html?scoLocation=${encodeURIComponent(scoLocation)}`,
    windowName,
    `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=${
      canBeResized ? 'yes' : 'no'
    }, dialog=${
      canBeMaximized ? 'yes' : 'no'
    }, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`
  )
}
